import React, { useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  Cell,
} from 'recharts';

export const PIPELINE_ORDER = [
  { value: 'draft',       label: 'Drafting',    color: '#1F2937' },
  { value: 'applied',     label: 'Applied',     color: '#374151' },
  { value: 'in_progress', label: 'In Progress', color: '#4B5563' },
  { value: 'awarded',     label: 'Awarded',     color: '#111827' },
  { value: 'rejected',    label: 'Rejected',    color: '#6B7280' },
];

interface AvgDaysPerStage {
  stage: string;
  avgDays: number;
}

interface StageCount {
  saved: number;
  draft: number;
  in_progress: number;
  applied: number;
  approved: number;
  awarded: number;
}

interface Conversion {
  draft_to_submitted: number;
  submitted_to_success: number;
}

export interface ApplicationStats {
  avgDaysPerStage: AvgDaysPerStage[];
  conversion: Conversion;
  stageCounts: StageCount;
  totalApplications: number;
}

interface Props {
  pipeline?: ApplicationStats;
  height?: number;
}

// ─────────────────────────────────────────────────────────────
// SKELETON for the chart
// ─────────────────────────────────────────────────────────────

function ChartSkeleton({ height }: { height: number }) {
  const bars = [55, 80, 45, 70, 35];
  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-4">
        <div className="h-5 w-36 bg-gray-200 rounded animate-pulse" />
        <div className="flex items-center gap-2">
          <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
          <div className="h-7 w-10 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>

      {/* Fake chart */}
      <div className="flex items-end gap-4 px-8" style={{ height }}>
        {bars.map((h, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-2">
            <div
              className="w-full bg-gray-200 rounded-t animate-pulse"
              style={{ height: `${h}%` }}
            />
            <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
          </div>
        ))}
      </div>

      {/* Y-axis label rows */}
      <div className="mt-4 flex justify-end gap-6 pr-4">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 bg-gray-300 rounded animate-pulse" />
          <div className="h-3 w-12 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 bg-green-200 rounded-full animate-pulse" />
          <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TOOLTIP
// ─────────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 text-sm">
      <p className="font-semibold text-gray-800 mb-2">{label}</p>
      {payload.map((entry: any, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.color }} />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-semibold text-gray-900">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN CHART COMPONENT
// ─────────────────────────────────────────────────────────────

const GrantPipelineHealthChart: React.FC<Props> = ({ height = 300, pipeline }) => {

  // Show skeleton while pipeline data hasn't arrived
  if (!pipeline) return <ChartSkeleton height={height} />;

  const data = PIPELINE_ORDER.map((d) => {
    const count   = pipeline?.stageCounts?.[d.value as keyof StageCount] ?? 0;
    const avgData = pipeline?.avgDaysPerStage.find((a) => a.stage === d.value);
    return { status: d.value, label: d.label, color: d.color, count, avgDays: avgData?.avgDays ?? 0 };
  });

  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const maxAvg   = Math.max(...data.map((d) => d.avgDays), 1);

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Grant Pipeline</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Total Applications</span>
          <span className="text-xl font-bold text-blue-600">{pipeline?.totalApplications ?? 0}</span>
        </div>
      </div>

      {/* Chart */}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} barCategoryGap="60%" barGap={9}
            margin={{ top: 8, right: 48, left: 0, bottom: 28 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
            <XAxis dataKey="label" height={50} interval={0} angle={-15} textAnchor="end"
              tick={{ fontSize: 12, fill: '#6B7280' }}>
              <Label value="Stage" offset={-16} position="insideBottom" style={{ fill: '#9CA3AF', fontSize: 12 }} />
            </XAxis>
            <YAxis yAxisId="left" allowDecimals={false}
              domain={[0, Math.max(5, Math.ceil(maxCount * 1.3))]}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              label={{ value: 'Count', angle: -90, position: 'insideLeft', offset: 8, style: { fill: '#6B7280', fontSize: 12 } }} />
            <YAxis yAxisId="right" orientation="right" allowDecimals={false}
              domain={[0, Math.max(5, Math.ceil(maxAvg * 1.3))]}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              label={{ value: 'Avg Days', angle: 90, position: 'insideRight', offset: 0, style: { fill: '#6B7280', fontSize: 12 } }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="top" align="right" height={18} wrapperStyle={{ fontSize: 13, paddingBottom: 41 }} />
            <Bar yAxisId="left" dataKey="count" name="Count" barSize={32} radius={[6, 6, 0, 0]} isAnimationActive={false}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.85} />
              ))}
            </Bar>
            <Line yAxisId="right" type="monotone" dataKey="avgDays" name="Avg Days"
              stroke="#10B981" strokeWidth={2.5}
              dot={{ r: 5, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }}
              activeDot={{ r: 7 }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GrantPipelineHealthChart;